<template>
    <component :is="layoutComponent">
        <!-- eslint-disable-next-line -->
        <div v-if="digistormSettings?.fontHtml" v-html="digistormSettings.fontHtml" />

        <template #popup>
            <slot name="popup" />
        </template>

        <template #navigation>
            <slot name="navigation" />
        </template>

        <template v-if="!isArticleType" #banner>
            <slot name="banner" />
        </template>

        <template v-if="isArticleType" #article-header>
            <component :is="articleHeaderComponent" />
        </template>

        <template #blocks>
            <slot name="blocks" />
        </template>

        <template v-if="isArticleType" #article-footer>
            <component :is="articleFooterComponent" />
        </template>

        <template #pre-footer>
            <slot name="pre-footer" />
        </template>

        <template #footer>
            <slot name="footer" />
        </template>
    </component>
</template>

<script setup>
    const pageType = inject('page-type', 'home')

    const route = useRoute()
    const siteStore = useSiteStore()

    const { entry, seomatic, digistormSettings, globalTheme } = storeToRefs(siteStore)

    const articleTypes = ref(['ds_News_Entry', 'ds_Events_Entry', 'ds_Newsletter_Entry', 'ds_NewsletterArticle_Entry'])

    const isArticleType = computed(() => {
        return articleTypes.value.includes(entry.value?.__typename)
    })

    const pageUri = computed(() => {
        return route.params.uri ? route.params.uri.join('/') : '__home__'
    })

    // return slug if this is an article route
    const articleSlug = computed(() => {
        if (!route.params.uri) {
            return
        }

        return route.params.uri.includes('newsletters') && route.params.uri.length === 3 ? last(route.params.uri) : null
    })

    await siteStore.loadPage(pageUri.value, articleSlug.value)

    const layoutComponent = ref()

    const roundness = computed(() => {
        switch (digistormSettings.value?.roundness) {
            case 'rounded':
                return 'soft'
            case 'circular':
                return 'round'
            case 'sharp':
                return 'sharp'
            default:
                return 'default'
        }
    })

    const articleHeaderComponent = defineAsyncComponent(() => {
        if (!isArticleType.value) {
            return
        }

        let name = entry.value.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        return import(`~/components/article-types/${name}/header.async.vue`)
    })

    const articleFooterComponent = defineAsyncComponent(() => {
        if (!isArticleType.value) {
            return
        }

        let name = entry.value.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        return import(`~/components/article-types/${name}/footer.async.vue`)
    })

    const seoTitle = computed(() => {
        const titleContainer = JSON.parse(seomatic.value?.metaTitleContainer ?? '{}')
        return get(titleContainer, 'title.title', entry.value?.title)
    })

    const metaTags = computed(() => {
        return map(JSON.parse(seomatic.value?.metaTagContainer), (tag) => {
            return {
                ...tag
            }
        })
    })

    let pageLayout = digistormSettings.value?.layoutType

    if (entry.value.__typename === 'ds_Newsletter_Entry') {
        pageLayout = 'newsletter'
    }

    layoutComponent.value = markRaw(defineAsyncComponent(() =>
        import(`./${pageType}/${pageLayout ?? 'layout-1'}/layout.async.vue`)
    ))

    // MicrositeHomepage should be treated like the homepage
    if (entry.value?.__typename === 'ds_MicrositeHomepage_Entry') {
        // TODO: post-relase patch, remove this check
        if (!['banner-1', 'banner-2'].includes(digistormSettings.value.bannerType) ){
            provide('page-type', 'home')
        }
    }

    useSeoMeta({
        title: seoTitle.value,
    })

    useHead({
        htmlAttrs: {
            'data-theme': globalTheme.value,
            'data-mode': `base radius-${roundness.value} button-${roundness.value}`,
            class: 'surface-background font-ux'
        },
        meta: metaTags.value
    })


</script>

<style lang="scss" scoped>

</style>